/**
 * Grid geometry: the flat virtual list and the column prefix sums (spec M1-B4 §6, §8.1).
 *
 * Everything here is pure arithmetic over constants, which is the whole point: the heights are
 * fixed, so no measurement pass is needed and the total scroll height is exact up front —
 * monday's own `table-component` sets `height: 27552px` before anything renders
 * `[trace:column-menu]`.
 *
 * Measured on `discovery/ui-reference/library/table-01-top.png` (1440 x 900, light):
 *   row pitch          36 px  (grid lines at y 320, 356, 392, 428, 464, 500, ...)
 *   group header       76 px  (title row 40 px at y 245-284 + column-header row 36 px)
 *   grid line          1 px   `#d0d4e4` = Vibe's `--layout-border-color`
 *   sticky region     698 px  (grid x 391 -> 1088): 6 px group bar + gutter + name + chat
 *   name cell         594 px  (x 430 -> 1023)
 *   conversation cell  65 px  (x 1024 -> 1088)
 *
 * **`name.width` is the sticky region, not the name cell** (parity card P-021, measured on
 * all ten boards of `discovery/ui-reference/parity/`): `391 + name.width` is the x of the
 * first scrolling column on every one of them, so the stored number already contains the
 * 6 px group bar, the 33 px checkbox rail and the 65 px conversation cell. Reading it as the
 * cell put every scrolling column 104 px (= 6 + 33 + 65) right of monday's. 698 above is that
 * board's stored `name.width`; 594 was the *cell* it leaves, which is why the two agreed.
 */
import type { ColumnId, GroupId, ItemId } from '@elizmonday/shared';

export const ROW_HEIGHT = 36;
export const GROUP_HEADER_HEIGHT = 76;
/**
 * A collapsed group is a **60 px strip** — the title over its item count, plus the columns'
 * summary cells drawn to the full grid width with their vertical rules — followed by 14 px of
 * air before the next group (parity card P-030, which answers Q146).
 *
 * Measured on `discovery/ui-reference/parity/{717364248,90397660}/group-collapsed-monday.png`:
 * the strip's rules are at y 205 and y 264 (60 px), the next group's title row starts at 279
 * and its column-header row at 319, so the collapsed group contributes `60 + 14 = 74` px —
 * which is exactly the height of monday's own `firstGroupHeader` box in `measurements.json`.
 */
export const GROUP_COLLAPSED_STRIP_HEIGHT = 60;
export const GROUP_COLLAPSED_GAP = 14;
export const GROUP_HEADER_COLLAPSED_HEIGHT = GROUP_COLLAPSED_STRIP_HEIGHT + GROUP_COLLAPSED_GAP;
export const GROUP_TITLE_ROW_HEIGHT = 40;
export const COLUMN_HEADER_ROW_HEIGHT = 36;
export const ADD_ITEM_ROW_HEIGHT = 36;
export const GROUP_FOOTER_HEIGHT = 36;
/** Vertical air after a group, before the next header. Measured 0 px; kept as a named zero. */
export const GROUP_GAP = 0;

/** Overscan, §6: 8 rows and 3 columns. */
export const ROW_OVERSCAN = 8;
export const COLUMN_OVERSCAN = 3;

/** Sticky-region parts, measured (§8.1). The menu gutter sits outside the grid. */
export const GROUP_BAR_WIDTH = 6;
/** 24 x 24, hover-only, **outside** the grid at x 363-387 `[B3§8.1]`. */
export const ROW_MENU_GUTTER = 24;
/** x 397-429 between the colour bar and the name cell; holds the 14 px checkbox. */
export const CHECKBOX_GUTTER = 33;
export const CONVERSATION_WIDTH = 65;
/**
 * What `name.width` carries **on top of** the name cell: the group bar, the checkbox rail and
 * the conversation cell all live inside the stored number (P-021). 6 + 33 + 65 = 104.
 */
export const NAME_STICKY_CHROME_WIDTH = GROUP_BAR_WIDTH + CHECKBOX_GUTTER + CONVERSATION_WIDTH;
/**
 * The default **sticky region**, not the default cell: 698 is the stored `name.width` of the
 * reference board (`[ui:library/table-01-top.png]`, x 391 -> 1088), whose name *cell* is the
 * 594 px this constant used to hold. P-021.
 */
export const NAME_COLUMN_DEFAULT_WIDTH = 698;
export const COLUMN_WIDTH_MIN = 60;
export const COLUMN_WIDTH_MAX = 1200;
/** A stored `name.width` can never squeeze the cell below `COLUMN_WIDTH_MIN`. */
export const NAME_COLUMN_MIN_WIDTH = NAME_STICKY_CHROME_WIDTH + COLUMN_WIDTH_MIN;

/**
 * The name **cell** inside a stored sticky-region width (P-021).
 *
 * `columnWidth(nameColumn)` is monday's number for the whole region measured from the grid's
 * left edge; every consumer that lays out a box — the rows, the group header, the footer, the
 * add-item row, the collapsed strip — needs what is left for the cell itself. The floor keeps
 * a hand-edited tiny width from producing a negative box; `NAME_COLUMN_MIN_WIDTH` is what the
 * resize handle clamps to, so the floor is only ever reached by data we did not write.
 */
export function nameCellWidth(stickyRegionWidth: number): number {
  return Math.max(COLUMN_WIDTH_MIN, stickyRegionWidth - NAME_STICKY_CHROME_WIDTH);
}

/* ── subitems (M3.S1 §2.5, measured on `[uis:02-row-expanded]`) ──────────────
 *
 *   parent row ends           y 357
 *   nested header row starts  y 373   -> 16 px of air
 *   nested cells start        x 423   -> 32 px of indent (the parent's are at x 391)
 *   header / subitem / add    36 px each, the same pitch as an item row
 *   add-subitem row ends      y 553
 *   next parent-level row     y 583   -> 30 px of air
 *
 * so an expanded parent contributes `118 + 36 n` px, which is what keeps the virtualiser's
 * total height exact with no measurement pass.
 */
export const SUBITEMS_GAP_TOP = 16;
export const SUBITEMS_GAP_BOTTOM = 30;
export const SUBITEMS_INDENT = 32;
export const SUBITEMS_HEADER_HEIGHT = 36;
export const SUBITEM_ROW_HEIGHT = 36;
export const ADD_SUBITEM_ROW_HEIGHT = 36;
/**
 * The nested name column, `x 423 w 300` `[uis:02-row-expanded]` — **300 in total**: the 6 px
 * group bar, the 32 px checkbox rail (the rail's right-hand rule is at x 461) and a 262 px
 * name cell out to x 723, which is where the Owner column starts. Round 5i read the 38 px rail
 * as sitting *on top of* the 300 and the block came out 44 px too wide (P-018).
 */
export const SUBITEM_NAME_WIDTH = 300;
/** The nested group-colour bar, x 423 -> 429. */
export const SUBITEMS_BAR_WIDTH = 6;
/** The nested checkbox rail, x 423 -> 461 — the bar is **inside** it. */
export const SUBITEM_CHECKBOX_GUTTER = 38;
/** What is left of the name column for the cell itself: x 461 -> 723. */
export const SUBITEM_NAME_CELL_WIDTH = SUBITEM_NAME_WIDTH - SUBITEM_CHECKBOX_GUTTER;

/* ── the connector (P-018, measured on `02-row-expanded.png`; the JSON's `tree` is null
 *    because monday paints it on the grid canvas) ──────────────────────────────────────
 *
 *   spine   1 px at x 393 = the grid's left edge + 2, from the parent row's bottom (y 357)
 *           straight through the block and the 30 px of air to the next row's top (y 583)
 *   bar     6 px at x 423-428, rounded 5 px top-left (y 373) and bottom-left (y 552)
 *   tick    1 px from the spine to the bar at each subitem row's `top + 17`, with a 5 px
 *           elbow out of the spine; the nested header has none
 *   alpha   1 on the header and the subitem rows, 0.5 on the `+ Add subitem` row — the
 *           sampled `rgb(254,213,158)` is `#fdab3d` at exactly 50 % on white
 */
export const SUBITEMS_SPINE_OFFSET = 2;
export const SUBITEMS_SPINE_WIDTH = 1;
/** The tick spans the spine to the nested bar: 393 -> 423. */
export const SUBITEMS_TICK_WIDTH = SUBITEMS_INDENT - SUBITEMS_SPINE_OFFSET;
/** Its horizontal run sits at `row top + 17` (y 426 on a row at y 409). */
export const SUBITEMS_TICK_DROP = 18;
export const SUBITEMS_CONNECTOR_RADIUS = 5;
/** The add-subitem row's bar and tick, and every other add row's bar `[uis:02]`. */
export const SUBITEMS_ADD_ROW_ALPHA = 0.5;
/**
 * **P-308** — the muted "subitems unavailable" line under a parent whose subitems read was
 * refused. It is **not** monday geometry (monday has no such state: its subitems board is never
 * another board's), so it is the grid's own smallest line: the 12 px secondary type on a 28 px
 * row, carrying no air of its own so an ordinary board's arithmetic is untouched.
 */
export const SUBITEMS_UNAVAILABLE_HEIGHT = 28;

/** The nested block's own left edge, relative to the parent grid's. */
export const subitemsLeft = (): number => SUBITEMS_INDENT;

/**
 * Width of the nested block's **columns**: the name column (rail included) plus every other
 * column of the subitems board. The rows themselves run on to the grid's full width — monday's
 * nested rules reach the right edge `[uis:02-row-expanded]`, P-018 — so this is the offset of
 * the `+ add column` cell, not the row's width. The caller passes widths, not columns, so this
 * file keeps no React import.
 */
export function subitemsWidthOf(otherColumnWidths: readonly number[]): number {
  return SUBITEM_NAME_WIDTH + otherColumnWidths.reduce((total, width) => total + width, 0);
}

/** Height an expanded parent adds below its own row: `118 + 36 n`. */
export function subitemsBlockHeight(subitemCount: number): number {
  return (
    SUBITEMS_GAP_TOP +
    SUBITEMS_HEADER_HEIGHT +
    subitemCount * SUBITEM_ROW_HEIGHT +
    ADD_SUBITEM_ROW_HEIGHT +
    SUBITEMS_GAP_BOTTOM
  );
}

export type FlatRowKind =
  | 'group-header'
  | 'row'
  | 'subitems-header'
  | 'subitem'
  | 'add-subitem'
  /** P-308: the parent stays collapsed and carries one muted line instead of a nested block. */
  | 'subitems-unavailable'
  | 'add-item'
  | 'group-footer';

export interface FlatRow {
  readonly kind: FlatRowKind;
  readonly groupId: GroupId;
  /** Only on `kind: 'row'` and `'subitem'` (the subitem's own id). */
  readonly itemId?: ItemId;
  /** Index of the item inside its group, for `aria-rowindex` arithmetic. */
  readonly indexInGroup?: number;
  /** The expanded parent a `subitems-header` / `subitem` / `add-subitem` row belongs to. */
  readonly parentItemId?: ItemId;
  /** Index of the subitem inside its parent. */
  readonly indexInParent?: number;
  readonly height: number;
  /**
   * `SUBITEMS_GAP_TOP` on the nested header and `SUBITEMS_GAP_BOTTOM` under the add-subitem
   * row — the air is carried by the row it precedes/follows rather than by phantom rows, so
   * the flat list stays one row per thing on screen.
   */
  readonly padTop?: number;
  readonly padBottom?: number;
}

export interface FlatGroupInput {
  readonly id: GroupId;
  readonly collapsed: boolean;
  readonly itemIds: readonly ItemId[];
  /** A group hides its add-item row when the actor cannot create items. */
  readonly canAddItem?: boolean;
  readonly showFooter?: boolean;
  /**
   * M3.S1: the subitem ids of every **expanded** parent in this group, in position order.
   * A parent that is absent (or collapsed) contributes nothing, so a board with no subitems
   * flattens exactly as it did before.
   */
  readonly expandedSubitems?: ReadonlyMap<ItemId, readonly ItemId[]>;
  /**
   * **P-308**: parents in this group whose subitems read was refused. They are collapsed — so
   * they contribute no nested block — and carry one `subitems-unavailable` line instead.
   */
  readonly subitemsUnavailable?: ReadonlySet<ItemId>;
}

/**
 * §6: `1` header + `n` rows + `1` add-item row + `1` footer per group, collapsed groups
 * contributing only their header. L6 falls straight out of the order.
 */
export function flatten(groups: readonly FlatGroupInput[], rowHeight = ROW_HEIGHT): FlatRow[] {
  const out: FlatRow[] = [];
  for (const group of groups) {
    if (group.collapsed) {
      out.push({ kind: 'group-header', groupId: group.id, height: GROUP_HEADER_COLLAPSED_HEIGHT });
      continue;
    }
    out.push({ kind: 'group-header', groupId: group.id, height: GROUP_HEADER_HEIGHT });
    group.itemIds.forEach((itemId, index) => {
      out.push({ kind: 'row', groupId: group.id, itemId, indexInGroup: index, height: rowHeight });
      const subitemIds = group.expandedSubitems?.get(itemId);
      if (subitemIds === undefined) {
        if (group.subitemsUnavailable?.has(itemId) === true) {
          out.push({
            kind: 'subitems-unavailable',
            groupId: group.id,
            parentItemId: itemId,
            height: SUBITEMS_UNAVAILABLE_HEIGHT,
          });
        }
        return;
      }
      out.push({
        kind: 'subitems-header',
        groupId: group.id,
        parentItemId: itemId,
        height: SUBITEMS_HEADER_HEIGHT,
        padTop: SUBITEMS_GAP_TOP,
      });
      subitemIds.forEach((subitemId, subIndex) => {
        out.push({
          kind: 'subitem',
          groupId: group.id,
          itemId: subitemId,
          parentItemId: itemId,
          indexInParent: subIndex,
          height: SUBITEM_ROW_HEIGHT,
        });
      });
      out.push({
        kind: 'add-subitem',
        groupId: group.id,
        parentItemId: itemId,
        height: ADD_SUBITEM_ROW_HEIGHT,
        padBottom: SUBITEMS_GAP_BOTTOM,
      });
    });
    if (group.canAddItem !== false) {
      out.push({ kind: 'add-item', groupId: group.id, height: ADD_ITEM_ROW_HEIGHT });
    }
    if (group.showFooter !== false) {
      out.push({ kind: 'group-footer', groupId: group.id, height: GROUP_FOOTER_HEIGHT });
    }
  }
  return out;
}

/** A row's own footprint: its height plus the air it carries (M3.S1). */
export const rowExtent = (row: FlatRow): number =>
  (row.padTop ?? 0) + row.height + (row.padBottom ?? 0);

export function totalHeight(rows: readonly FlatRow[]): number {
  return rows.reduce((sum, row) => sum + rowExtent(row), 0);
}

/** `top` of every flat row — L1: row *n* sits at `header.top + 76 + n * 36`. */
export function offsets(rows: readonly FlatRow[]): number[] {
  const out: number[] = new Array(rows.length);
  let top = 0;
  for (let index = 0; index < rows.length; index += 1) {
    // `top` is where the row itself paints, i.e. **after** its own leading air.
    top += rows[index]!.padTop ?? 0;
    out[index] = top;
    top += rows[index]!.height + (rows[index]!.padBottom ?? 0);
  }
  return out;
}

/* ── columns ──────────────────────────────────────────────────────────────── */

export interface ColumnLayout {
  readonly id: ColumnId;
  readonly left: number;
  readonly width: number;
}

/** Prefix sum of widths; the sticky region is laid out separately and never enters this. */
export function layoutColumns(columns: readonly { id: ColumnId; width: number }[]): {
  layout: ColumnLayout[];
  total: number;
} {
  const layout: ColumnLayout[] = [];
  let left = 0;
  for (const column of columns) {
    layout.push({ id: column.id, left, width: column.width });
    left += column.width;
  }
  return { layout, total: left };
}

/**
 * Which columns intersect `[scrollLeft, scrollLeft + viewport]`, with `COLUMN_OVERSCAN` either
 * side. Binary search, so L4 (84 columns, jump to the last one in < 100 ms) is a non-event.
 */
export function visibleColumnRange(
  layout: readonly ColumnLayout[],
  scrollLeft: number,
  viewportWidth: number,
  overscan = COLUMN_OVERSCAN,
): { start: number; end: number } {
  if (layout.length === 0) return { start: 0, end: 0 };
  const start = Math.max(0, lowerBound(layout, scrollLeft) - overscan);
  const end = Math.min(layout.length, lowerBound(layout, scrollLeft + viewportWidth) + 1 + overscan);
  return { start, end };
}

/** Index of the first column whose right edge is past `x`. */
function lowerBound(layout: readonly ColumnLayout[], x: number): number {
  let low = 0;
  let high = layout.length - 1;
  let answer = layout.length;
  while (low <= high) {
    const mid = (low + high) >> 1;
    const column = layout[mid]!;
    if (column.left + column.width > x) {
      answer = mid;
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }
  return Math.min(answer, layout.length - 1);
}

/**
 * Resize clamp (ASSUMED, Q165). `min` is the name column's `NAME_COLUMN_MIN_WIDTH`, because a
 * drag there writes the whole sticky region and 60 px of region would leave no cell (P-021).
 */
export function clampWidth(width: number, min: number = COLUMN_WIDTH_MIN): number {
  return Math.min(COLUMN_WIDTH_MAX, Math.max(min, Math.round(width)));
}

/**
 * §8.13 "Zoom": below 700 px of grid width only the name column stays sticky (E12).
 *
 * **P-063.** It used to drop the 33 px checkbox rail too — and `table.css` hid the rail's
 * `···` with it — which is 24 px saved at the cost of *every* item action. With the sidebar
 * open and the card panel's allowance a 1024 × 768 window is already under this threshold, so
 * an ordinary laptop had no row menu, no row checkbox, therefore no multi-select bar, and (the
 * item card offering no actions either, P-060) no way at all to archive, delete, duplicate or
 * move a row. The conservative reading of a rule written for phones is to keep the only path
 * to the actions and collapse the 65 px conversation cell alone; monday's own 1024 behaviour
 * is not captured (**q494**).
 */
export const NARROW_GRID_WIDTH = 700;

export function stickyWidth(nameWidth: number, extraPinned: number, gridWidth: number): number {
  if (gridWidth < NARROW_GRID_WIDTH) return GROUP_BAR_WIDTH + CHECKBOX_GUTTER + nameWidth;
  return GROUP_BAR_WIDTH + CHECKBOX_GUTTER + nameWidth + CONVERSATION_WIDTH + extraPinned;
}

/**
 * Below this grid width the name column is clamped (M0-F9 §5, F9-D5). 480 sits between the
 * widest phone in the reference set (Pixel 7, 412) and the narrowest tablet, so a desktop
 * window narrowed to 700 px still renders the measured 594 and no capture moves.
 */
export const PHONE_GRID_WIDTH = 480;

/** Pixels kept for the first scrolling column, so a phone never shows the name column alone. */
export const PHONE_NAME_RESERVE = 148;

/**
 * The width the name column actually renders at.
 *
 * monday has no mobile web to copy (`discovery/ui-reference/mobile/INDEX.md`), and its
 * measured default cell is **594** (a 698 px sticky region) — wider than any phone. Left alone, the sticky region covers the
 * whole viewport, the horizontal scroll reveals nothing, and every data cell is permanently
 * off screen: the phone board shows names and nothing else. So below `PHONE_GRID_WIDTH` the
 * name is clamped to leave one data column visible, with a 120 px floor so the clamp can never
 * squeeze the name itself away.
 *
 * It is clamped **here**, on the one number every consumer reads — rows, group headers, the
 * column-header strip, the footer and the add-item row all take `nameWidth` from the same
 * place — because clamping the boxes in CSS moves the sticky region while the absolutely
 * positioned data cells keep their old offsets, which is misalignment, not a narrower column.
 */
export function nameColumnWidth(measured: number, gridWidth: number): number {
  if (gridWidth <= 0 || gridWidth >= PHONE_GRID_WIDTH) return measured;
  return Math.max(120, Math.min(measured, gridWidth - PHONE_NAME_RESERVE));
}
